import React from "react";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";

/**
 * MasciLogo — ForgedOps logo system.
 *
 * Kept the export name for backwards-compatibility (40+ import sites).
 * Internally renders the canonical ForgedOps assets:
 *   - Emblem PNG (compact / mark / favicon contexts)
 *   - Horizontal lockup PNG (default wordmark / nav / footer)
 *   - Vertical lockup PNG (login splash / hero / training intro / PDF cover)
 *
 * Tagline (when shown) is the current ForgedOps positioning line —
 * "Building Safer Jobs · Powering Performance" — never the legacy
 * "No guesswork / No missed steps / No excuses" line.
 *
 * Variants:
 *   - mark      — emblem PNG + wordmark (compact header / sidebar)
 *   - wordmark  — horizontal lockup only (default nav)
 *   - lockup    — vertical lockup PNG (splash / login / training intro)
 */

const HEIGHT_MAP = {
  sm:    { px: 28,  text: "text-base sm:text-lg",  tag: "text-[8px]"  },
  md:    { px: 36,  text: "text-lg sm:text-xl",    tag: "text-[9px]"  },
  lg:    { px: 44,  text: "text-xl sm:text-2xl",   tag: "text-[10px]" },
  xl:    { px: 56,  text: "text-2xl sm:text-3xl",  tag: "text-[11px]" },
  "2xl": { px: 80,  text: "text-3xl sm:text-4xl",  tag: "text-xs"     },
  "3xl": { px: 96,  text: "text-4xl sm:text-5xl",  tag: "text-sm"     },
  "4xl": { px: 112, text: "text-5xl sm:text-6xl",  tag: "text-sm"     },
  "5xl": { px: 128, text: "text-6xl sm:text-7xl",  tag: "text-base"   },
};

const TAGLINE = "Building Safer Jobs · Powering Performance";

const EMBLEM_SRC     = "/forgedops-emblem.png";
const HORIZONTAL_SRC = "/forgedops-horizontal.png";
const VERTICAL_SRC   = "/forgedops-vertical.png";

export const MasciLogo = ({
  variant = "wordmark",
  className = "",
  size = "md",
  onLight = false,
  homeLink = null,
}) => {
  const sz = HEIGHT_MAP[size] || HEIGHT_MAP.md;
  const tagColor = onLight ? "text-slate-700" : "text-slate-300";

  let body;

  if (variant === "mark") {
    // Emblem + horizontal wordmark — used in compact headers / sidebars
    body = (
      <span
        className={cn("inline-flex items-center gap-2.5 select-none", className)}
        data-testid="masci-logo-mark"
      >
        <img
          src={EMBLEM_SRC}
          alt="ForgedOps"
          height={sz.px}
          style={{ height: sz.px, width: "auto", display: "block" }}
          draggable={false}
          loading="eager"
        />
      </span>
    );
  } else if (variant === "lockup") {
    // Vertical lockup — login splash / hero / training intro / PDF cover
    body = (
      <span
        className={cn("inline-flex flex-col items-start gap-1.5 select-none", className)}
        data-testid="masci-logo-lockup"
      >
        <img
          src={VERTICAL_SRC}
          alt="ForgedOps"
          height={sz.px}
          style={{ height: sz.px, width: "auto", display: "block" }}
          draggable={false}
          loading="eager"
        />
        <span
          className={cn(
            "font-mono uppercase tracking-[0.3em] leading-none",
            sz.tag,
            tagColor,
          )}
        >
          {TAGLINE}
        </span>
      </span>
    );
  } else {
    // wordmark (default) — horizontal lockup PNG
    body = (
      <span
        className={cn("inline-flex items-center select-none", className)}
        data-testid="masci-logo-default"
      >
        <img
          src={HORIZONTAL_SRC}
          alt="ForgedOps"
          height={sz.px}
          style={{ height: sz.px, width: "auto", display: "block" }}
          draggable={false}
          loading="eager"
        />
      </span>
    );
  }

  if (homeLink) {
    return (
      <Link
        to={homeLink}
        aria-label="ForgedOps home"
        className="inline-block focus:outline-none focus:ring-2 focus:ring-orange-500 rounded"
        data-testid="masci-logo-home-link"
      >
        {body}
      </Link>
    );
  }
  return body;
};

export default MasciLogo;
